import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { updateIntake } from '../services/api';
import { useToast } from '../hooks/useToastContext';
import { IntakeRecord } from '@/store/slices/intakeSlice';
import {
  assigneeDisplayName,
  useIntakeAssignableUsers,
} from '../hooks/useIntakeAssignableUsers';

interface IntakeUserAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intakes: IntakeRecord[];
  onSuccess?: () => void;
}

export const IntakeUserAssignmentDialog: React.FC<
  IntakeUserAssignmentDialogProps
> = ({ open, onOpenChange, intakes, onSuccess }) => {
  const intakeIds = intakes.map(intake => intake.id);
  const { error: showError } = useToast();
  const { options, isLoading: usersLoading } = useIntakeAssignableUsers(open);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Reset selection when dialog opens; pre-populate if all intakes share the same assignee
  useEffect(() => {
    if (open) {
      const firstAssignedTo = intakes[0]?.assigned_to;
      const allSameAssignee =
        intakes.length > 0 &&
        (firstAssignedTo ?? '') !== '' &&
        intakes.every(i => (i.assigned_to ?? '') === firstAssignedTo);
      setSelectedUserId(allSameAssignee ? (firstAssignedTo as string) : '');
    }
  }, [open, intakes]);

  const handleAssign = async () => {
    setIsAssigning(true);
    try {
      await Promise.all(
        intakeIds.map(intakeId =>
          updateIntake({
            path: { intake_id: intakeId },
            body: { assigned_to: selectedUserId },
          })
        )
      );
      onOpenChange(false);
      onSuccess?.();
    } catch {
      showError(
        'Failed to assign intakes',
        'Please try again or refresh the page.'
      );
    } finally {
      setIsAssigning(false);
    }
  };

  // Seed options with current assignee so the label shows immediately (before sortedOptions loads)
  const prefillAssignee =
    intakes.length > 0 &&
    intakes[0]?.assigned_to &&
    intakes.every(i => i.assigned_to === intakes[0].assigned_to)
      ? intakes[0].assigned_to_user
      : null;
  const prefillOption = prefillAssignee?.id
    ? {
        value: prefillAssignee.id,
        label: assigneeDisplayName(prefillAssignee),
      }
    : null;
  const userOptions =
    prefillOption && !options.some(o => o.value === prefillOption.value)
      ? [prefillOption, ...options]
      : options;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Intakes</DialogTitle>
          <DialogDescription>
            Select a user to assign {intakeIds.length} intake
            {intakeIds.length > 1 ? 's' : ''} to.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          <VirtualizedFormSelect
            key={open ? 'assign-user-select' : 'closed'}
            label='Select User'
            placeholder='Select a user'
            options={userOptions}
            sortOptions
            defaultValue={selectedUserId}
            onChange={e => setSelectedUserId(e.target.value)}
            disabled={usersLoading}
            isClearable={false}
            menuPortalTarget={null}
            menuPosition='absolute'
          />
        </div>

        <DialogFooter>
          <Button
            onClick={handleAssign}
            disabled={isAssigning || !selectedUserId}
          >
            {isAssigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
